import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  Store, Plus, MapPin, Phone, Clock, TrendingUp,
  Package, AlertTriangle, ArrowRightLeft, ChevronDown,
  Edit2, X, Check, BarChart3
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STORE_TYPES = [
  { value: 'smoke_shop', label: 'Smoke Shop' },
  { value: 'gas_station', label: 'Gas Station' },
  { value: 'convenience', label: 'Convenience Store' },
  { value: 'retail', label: 'Retail' },
];

export default function MultiStoreDashboard() {
  const { token } = useAuth();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    name: '', address: '', city: '', state: '', zip_code: '',
    phone: '', store_type: 'smoke_shop', brand: '', manager_name: '', operating_hours: ''
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchStores = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/stores`, { headers });
      setStores(res.data);
    } catch (err) {
      console.error('Failed to fetch stores:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchStores(); }, [fetchStores]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Store name is required'); return; }
    setSubmitting(true);
    try {
      if (editingStore) {
        await axios.put(`${API_URL}/api/stores/${editingStore}`, form, { headers });
        toast.success('Store updated');
      } else {
        await axios.post(`${API_URL}/api/stores`, form, { headers });
        toast.success('Store created');
      }
      setShowAddForm(false);
      setEditingStore(null);
      setForm({ name: '', address: '', city: '', state: '', zip_code: '', phone: '', store_type: 'smoke_shop', brand: '', manager_name: '', operating_hours: '' });
      fetchStores();
    } catch (err) {
      toast.error('Failed to save store');
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (store) => {
    setEditingStore(store.id);
    setForm({
      name: store.name || '', address: store.address || '', city: store.city || '',
      state: store.state || '', zip_code: store.zip_code || '', phone: store.phone || '',
      store_type: store.store_type || 'smoke_shop', brand: store.brand || '',
      manager_name: store.manager_name || '', operating_hours: store.operating_hours || ''
    });
    setShowAddForm(true);
  };

  const cancelForm = () => {
    setShowAddForm(false);
    setEditingStore(null);
    setForm({ name: '', address: '', city: '', state: '', zip_code: '', phone: '', store_type: 'smoke_shop', brand: '', manager_name: '', operating_hours: '' });
  };

  const getTypeLabel = (type) => STORE_TYPES.find(t => t.value === type)?.label || type;

  const getTypeColor = (type) => {
    const colors = {
      smoke_shop: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
      gas_station: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
      convenience: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
      retail: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    };
    return colors[type] || colors.retail;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="stores-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="multi-store-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground" data-testid="page-title">Store Management</h1>
          <p className="text-sm text-muted-foreground mt-1">{stores.length} store{stores.length !== 1 ? 's' : ''} across your organization</p>
        </div>
        <div className="flex items-center gap-3">
          <a href="/inventory-transfers"
            className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors font-medium text-sm"
            data-testid="transfers-link">
            Transfers
          </a>
          <button
            onClick={() => { cancelForm(); setShowAddForm(true); }}
            className="flex items-center gap-2 px-4 py-2.5 bg-purple-600 hover:bg-purple-500 text-white rounded-lg transition-colors font-medium"
            data-testid="add-store-btn"
          >
            <Plus className="w-4 h-4" />
            Add Store
          </button>
        </div>
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="bg-card border border-border rounded-xl p-6" data-testid="store-form">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-foreground">
              {editingStore ? 'Edit Store' : 'Add New Store'}
            </h2>
            <button onClick={cancelForm} className="text-muted-foreground hover:text-foreground">
              <X className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-muted-foreground mb-1">Store Name *</label>
              <input
                type="text" required value={form.name}
                onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Broadway Smoke Shop"
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-foreground placeholder-muted-foreground"
                data-testid="store-name-input"
              />
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-1">Store Type</label>
              <select
                value={form.store_type}
                onChange={(e) => setForm(p => ({ ...p, store_type: e.target.value }))}
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-foreground"
                data-testid="store-type-select"
              >
                {STORE_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-1">Brand</label>
              <input
                type="text" value={form.brand}
                onChange={(e) => setForm(p => ({ ...p, brand: e.target.value }))}
                placeholder="e.g. Phillips 66"
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-foreground placeholder-muted-foreground"
                data-testid="store-brand-input"
              />
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-1">Address</label>
              <input
                type="text" value={form.address}
                onChange={(e) => setForm(p => ({ ...p, address: e.target.value }))}
                placeholder="1489 Broadway"
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-foreground placeholder-muted-foreground"
                data-testid="store-address-input"
              />
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-1">City</label>
              <input
                type="text" value={form.city}
                onChange={(e) => setForm(p => ({ ...p, city: e.target.value }))}
                placeholder="Gary"
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-foreground placeholder-muted-foreground"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm text-muted-foreground mb-1">State</label>
                <input
                  type="text" value={form.state}
                  onChange={(e) => setForm(p => ({ ...p, state: e.target.value }))}
                  placeholder="IN"
                  className="w-full bg-background border border-border rounded-lg px-3 py-2 text-foreground placeholder-muted-foreground"
                />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-1">ZIP</label>
                <input
                  type="text" value={form.zip_code}
                  onChange={(e) => setForm(p => ({ ...p, zip_code: e.target.value }))}
                  placeholder="46407"
                  className="w-full bg-background border border-border rounded-lg px-3 py-2 text-foreground placeholder-muted-foreground"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-1">Phone</label>
              <input
                type="text" value={form.phone}
                onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))}
                placeholder="(219) 883-3310"
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-foreground placeholder-muted-foreground"
              />
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-1">Manager</label>
              <input
                type="text" value={form.manager_name}
                onChange={(e) => setForm(p => ({ ...p, manager_name: e.target.value }))}
                placeholder="Manager name"
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-foreground placeholder-muted-foreground"
              />
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-1">Operating Hours</label>
              <input
                type="text" value={form.operating_hours}
                onChange={(e) => setForm(p => ({ ...p, operating_hours: e.target.value }))}
                placeholder="6 AM - 11 PM"
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-foreground placeholder-muted-foreground"
              />
            </div>
            <div className="md:col-span-2 lg:col-span-3 flex gap-3">
              <button
                type="submit" disabled={submitting}
                className="px-6 py-2.5 bg-purple-600 hover:bg-purple-500 text-white rounded-lg transition-colors font-medium disabled:opacity-50"
                data-testid="save-store-btn"
              >
                {submitting ? 'Saving...' : editingStore ? 'Update Store' : 'Create Store'}
              </button>
              <button type="button" onClick={cancelForm}
                className="px-6 py-2.5 bg-muted hover:bg-accent text-muted-foreground rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Store Cards */}
      {stores.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-8 text-center" data-testid="no-stores">
          <Store className="w-16 h-16 text-muted-foreground/40 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground">No Stores Yet</h3>
          <p className="text-sm text-muted-foreground mt-2">Add your first store to start managing multi-location operations</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="store-cards-grid">
          {stores.map((store) => (
            <div
              key={store.id}
              className="bg-card border border-border rounded-xl p-5 hover:border-primary/40 transition-colors group"
              data-testid={`store-card-${store.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-bold text-foreground text-lg">{store.name}</h3>
                  <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium border ${getTypeColor(store.store_type)}`}>
                    {getTypeLabel(store.store_type)}
                  </span>
                </div>
                <button
                  onClick={() => startEdit(store)}
                  className="text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`edit-store-${store.id}`}
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              </div>

              {store.brand && (
                <p className="text-sm text-muted-foreground mb-2">{store.brand}</p>
              )}

              <div className="space-y-1.5 text-sm text-muted-foreground">
                {store.address && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">{store.address}{store.city ? `, ${store.city}` : ''}{store.state ? `, ${store.state}` : ''} {store.zip_code || ''}</span>
                  </div>
                )}
                {store.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-3.5 h-3.5" />
                    <span>{store.phone}</span>
                  </div>
                )}
                {store.operating_hours && (
                  <div className="flex items-center gap-2">
                    <Clock className="w-3.5 h-3.5" />
                    <span>{store.operating_hours}</span>
                  </div>
                )}
              </div>

              {store.manager_name && (
                <p className="text-xs text-muted-foreground mt-3">Manager: {store.manager_name}</p>
              )}

              {!store.is_active && (
                <div className="mt-3 px-2 py-1 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-500 inline-block">
                  Inactive
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
